// src/admin/pages/review/ReviewsManagement.tsx
import React, { useState, useRef } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Alert,
  CircularProgress,
  Stack,
  Chip,
  Tooltip,
  Drawer,
  Divider,
  Rating,
  Avatar,
  TextField,
  InputAdornment,
  Pagination,
  Collapse,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Backdrop,
  LinearProgress,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  RateReview as RateReviewIcon,
  Visibility as VisibilityIcon,
  Close as CloseIcon,
  Star as StarIcon,
  Person as PersonIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Clear as ClearIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import ReviewsIcon from '@mui/icons-material/Reviews';
import { Review } from './reviewService';
import {
  useReviewsPaginated,
  useReviewStats,
  useReviewMutations,
} from '../../../hooks/useReviews';
import ToastNotification from '../common/Toastnotification';
import { useAuth } from '../../../contexts/AuthContext';

const REVIEWS_PER_PAGE = 10;

interface ToastState {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'warning' | 'info';
}

const getSoftChipSx = (color: string, isDark: boolean = false) => {
  type PaletteEntry = { bg: string; border: string; text: string; iconColor: string };

  const lightPalette: Record<string, PaletteEntry> = {
    warning: { bg: 'rgba(255,167,38,0.12)', border: 'rgba(255,167,38,0.4)', text: '#b76e00', iconColor: '#e68900' },
    info: { bg: 'rgba(41,182,246,0.12)', border: 'rgba(41,182,246,0.4)', text: '#0277bd', iconColor: '#039be5' },
    primary: { bg: 'rgba(33,150,243,0.12)', border: 'rgba(33,150,243,0.4)', text: '#1565c0', iconColor: '#1976d2' },
    success: { bg: 'rgba(102,187,106,0.15)', border: 'rgba(102,187,106,0.45)', text: '#2e7d32', iconColor: '#43a047' },
    error: { bg: 'rgba(239,83,80,0.12)', border: 'rgba(239,83,80,0.4)', text: '#c62828', iconColor: '#e53935' },
    secondary: { bg: 'rgba(171,71,188,0.10)', border: 'rgba(171,71,188,0.35)', text: '#6a1b9a', iconColor: '#8e24aa' },
    default: { bg: 'rgba(158,158,158,0.12)', border: 'rgba(158,158,158,0.4)', text: '#424242', iconColor: '#757575' },
  };

  const darkPalette: Record<string, PaletteEntry> = {
    warning: { bg: 'rgba(255,167,38,0.22)', border: 'rgba(255,167,38,0.55)', text: '#ffcc80', iconColor: '#ffa726' },
    info: { bg: 'rgba(41,182,246,0.20)', border: 'rgba(41,182,246,0.50)', text: '#81d4fa', iconColor: '#29b6f6' },
    primary: { bg: 'rgba(33,150,243,0.22)', border: 'rgba(33,150,243,0.50)', text: '#90caf9', iconColor: '#42a5f5' },
    success: { bg: 'rgba(102,187,106,0.22)', border: 'rgba(102,187,106,0.50)', text: '#a5d6a7', iconColor: '#66bb6a' },
    error: { bg: 'rgba(239,83,80,0.22)', border: 'rgba(239,83,80,0.50)', text: '#ef9a9a', iconColor: '#ef5350' },
    secondary: { bg: 'rgba(171,71,188,0.22)', border: 'rgba(171,71,188,0.50)', text: '#ce93d8', iconColor: '#ab47bc' },
    default: { bg: 'rgba(158,158,158,0.22)', border: 'rgba(158,158,158,0.50)', text: '#e0e0e0', iconColor: '#bdbdbd' },
  };

  const palette = isDark ? darkPalette : lightPalette;
  const p = palette[color] ?? palette.default;

  return {
    backgroundColor: p.bg,
    borderColor: p.border,
    color: p.text,
    fontWeight: 600,
    border: `1px solid ${p.border}`,
    '& .MuiChip-icon': { color: p.iconColor },
    '& .MuiChip-label': { color: p.text },
  };
};

const getRatingChipColor = (rating: number): string => {
  if (rating === 5) return 'success';
  if (rating === 4) return 'primary';
  if (rating === 3) return 'info';
  if (rating === 2) return 'warning';
  return 'error';
};

interface LoadingOverlayProps {
  wrapperRef: React.RefObject<HTMLDivElement | null>;
}

const LoadingOverlay: React.FC<LoadingOverlayProps> = ({ wrapperRef }) => {
  const [rect, setRect] = useState<DOMRect | null>(null);
  const theme = useTheme();

  const overlayBg = theme.palette.mode === 'dark'
    ? 'rgba(26, 29, 36, 0.85)'
    : 'rgba(255, 255, 255, 0.85)';

  React.useEffect(() => {
    const measure = () => {
      if (wrapperRef.current) setRect(wrapperRef.current.getBoundingClientRect());
    };
    measure();
    const el = wrapperRef.current;
    window.addEventListener('resize', measure);
    el?.addEventListener('scroll', measure);
    return () => {
      window.removeEventListener('resize', measure);
      el?.removeEventListener('scroll', measure);
    };
  }, [wrapperRef]);

  if (!rect) return null;

  return (
    <Box
      sx={{
        position: 'fixed',
        top: rect.top, left: rect.left,
        width: rect.width, height: rect.height,
        backgroundColor: overlayBg,
        backdropFilter: 'blur(2px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1100, pointerEvents: 'all', borderRadius: 'inherit',
      }}
    >
      <Box sx={{ textAlign: 'center' }}>
        <CircularProgress size={40} />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Loading reviews...
        </Typography>
      </Box>
    </Box>
  );
};

const ReviewsManagement: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isDark = theme.palette.mode === 'dark';

  // ── UI state ──────────────────────────────────────────────────────────────
  const [deleteLoading, setDeleteLoading] = useState<string>('');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedReview, setSelectedReview] = useState<Review | null>(null);
  const [toast, setToast] = useState<ToastState>({ open: false, message: '', severity: 'info' });
  const [deleteTarget, setDeleteTarget] = useState<Review | null>(null);
  const [deleteDialogLoading, setDeleteDialogLoading] = useState(false);

  // ── Full-screen delete backdrop (mirrors EnhancedProductDrawer pattern) ──
  const [deletingOverlay, setDeletingOverlay] = useState(false);

  // ── Pagination & filter state ─────────────────────────────────────────────
  const [currentPage, setCurrentPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [ratingFilter, setRatingFilter] = useState<string>('all');
  const [showFilters, setShowFilters] = useState(false);

  const tableWrapperRef = useRef<HTMLDivElement>(null);

  const { hasPermission, isSuperAdmin } = useAuth();

const canView   = isSuperAdmin || hasPermission('reviews.view');
const canManage = isSuperAdmin || hasPermission('reviews.manage'); // delete

  // ── React Query hooks ─────────────────────────────────────────────────────
  const {
    data: reviewsData,
    isLoading: reviewsLoading,
    isFetching: reviewsIsFetching,
    isError: reviewsIsError,
    error: reviewsErrorObj,
  } = useReviewsPaginated({
    page: currentPage,
    search: searchQuery,
    rating: ratingFilter,
    pageSize: REVIEWS_PER_PAGE,
  });

  const { data: statsData } = useReviewStats();
  const { remove } = useReviewMutations();

  // ── Derived values ────────────────────────────────────────────────────────
  const reviews = reviewsData?.reviews ?? [];
  const totalReviews = reviewsData?.totalCount ?? 0;
  const totalPages = Math.ceil(totalReviews / REVIEWS_PER_PAGE);
  const stats = statsData ?? {
    total: 0,
    averageRating: 0,
    ratingDistribution: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 },
  };
  const loading = reviewsLoading || reviewsIsFetching;

  // ── Helpers ───────────────────────────────────────────────────────────────
  const showToast = (message: string, severity: 'success' | 'error' | 'warning' | 'info') => {
    setToast({ open: true, message, severity });
  };
  const handleCloseToast = () => setToast(prev => ({ ...prev, open: false }));

  const hasActiveFilters = searchQuery !== '' || ratingFilter !== 'all';
  const activeFiltersCount = [searchQuery !== '', ratingFilter !== 'all'].filter(Boolean).length;

  // ── Reset to page 1 ───────────────────────────────────────────────────────
  const resetToFirstPage = () => {
    setCurrentPage(1);
  };

  // ── Pagination handler ────────────────────────────────────────────────────
  const handlePageChange = (_event: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
  };

  // ── Search ────────────────────────────────────────────────────────────────
  const handleSearchKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      setSearchQuery(searchInput);
      resetToFirstPage();
    }
  };

  // ── Filters ───────────────────────────────────────────────────────────────
  const handleRatingFilterChange = (value: string) => {
    setRatingFilter(value);
    resetToFirstPage();
  };

  const clearFilters = () => {
    setSearchInput('');
    setSearchQuery('');
    setRatingFilter('all');
    resetToFirstPage();
    showToast('Filters cleared', 'info');
  };

  // ── Delete ────────────────────────────────────────────────────────────────
  const handleDeleteReview = (review: Review) => {
    setDeleteTarget(review);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    try {
      setDeleteDialogLoading(true);
      setDeleteLoading(deleteTarget.id!);
      // Close the confirm dialog immediately, then show the full-screen overlay
      // while the async work completes — mirrors the EnhancedProductDrawer pattern.
      setDeleteTarget(null);
      setDeletingOverlay(true);

      await remove.mutateAsync(deleteTarget.id!);

      if (selectedReview?.id === deleteTarget.id) {
        setDrawerOpen(false);
        setSelectedReview(null);
      }
      resetToFirstPage();
      showToast('Review deleted successfully', 'success');
    } catch (err: any) {
      showToast(`Failed to delete review: ${err.message || 'Unknown error'}`, 'error');
    } finally {
      setDeleteDialogLoading(false);
      setDeleteLoading('');
      setDeletingOverlay(false);
    }
  };

  const handleDeleteCancel = () => setDeleteTarget(null);

  // ── Drawer ────────────────────────────────────────────────────────────────
  const handleViewReview = (review: Review) => {
    setSelectedReview(review);
    setDrawerOpen(true);
  };

  if (!canView) {
  return (
    <Box sx={{ p: 3 }}>
      <Card>
        <CardContent sx={{ textAlign: 'center', py: 6 }}>
          <ReviewsIcon sx={{ fontSize: 64, color: 'warning.main', mb: 2, opacity: 0.7 }} />
          <Typography variant="h5" gutterBottom>Access Denied</Typography>
          <Typography variant="body1" color="text.secondary">
            You don't have permission to view reviews. Please contact your administrator.
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
}

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setSelectedReview(null);
  };

  // ── Initial full-page loading state ───────────────────────────────────────
  if (loading && reviews.length === 0) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" minHeight="400px">
        <CircularProgress size={40} />
        <Typography variant="body1" sx={{ ml: 2 }}>Loading reviews...</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: { md: '100%' },
        display: 'flex',
        flexDirection: 'column',
        overflow: { xs: 'visible', md: 'hidden' },
        p: { xs: 2, sm: 3 },
      }}
    >
      {/* ── Fixed Header ── */}
      <Box sx={{ flexShrink: 0 }}>
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
          <ReviewsIcon sx={{ fontSize: { xs: 28, md: 32 }, color: 'primary.main' }} />
          <Box>
            <Typography variant="h4" component="h1" sx={{ fontSize: { xs: '1.5rem', md: '2rem' } }}>
              Product Reviews
            </Typography>
            <Typography variant="body2" color="text.secondary">
              View and manage customer reviews
            </Typography>
          </Box>
        </Stack>

        {/* ── Stats Cards ── */}
        <Grid container spacing={{ xs: 1, sm: 1.5, md: 2 }} sx={{ mb: 2 }} alignItems="stretch">

          <Grid item xs={6} sm={6} md={3} sx={{ display: 'flex' }}>
            <Card sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ p: { xs: 1, sm: 1.25 }, '&:last-child': { pb: { xs: 1, sm: 1.25 } }, flex: 1, display: 'flex', flexDirection: 'column' }}>
                <Typography color="text.secondary" gutterBottom variant="caption" noWrap>Total Reviews</Typography>
                <Typography variant="h5" component="div" color="text.primary"
                  sx={{ fontSize: { xs: '1rem', sm: '1.125rem', md: '1.375rem' }, mb: 0.5 }}>
                  {stats.total}
                </Typography>
                <Box sx={{ mt: 'auto', display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'success.main', flexShrink: 0 }} />
                    <Typography variant="caption" color="success.main" noWrap>
                      {stats.ratingDistribution[5]} five-star
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'error.main', flexShrink: 0 }} />
                    <Typography variant="caption" color="error.main" noWrap>
                      {stats.ratingDistribution[1]} one-star
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={6} sm={6} md={3} sx={{ display: 'flex' }}>
            <Card sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ p: { xs: 1, sm: 1.25 }, '&:last-child': { pb: { xs: 1, sm: 1.25 } }, flex: 1, display: 'flex', flexDirection: 'column' }}>
                <Typography color="text.secondary" gutterBottom variant="caption" noWrap>Average Rating</Typography>
                <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 1 }}>
                  <Typography variant="h5" component="div" color="text.primary"
                    sx={{ fontSize: { xs: '1rem', sm: '1.125rem', md: '1.375rem' } }}>
                    {stats.averageRating.toFixed(1)}
                  </Typography>
                  <StarIcon sx={{ color: 'warning.main', fontSize: { xs: 18, sm: 22, md: 28 } }} />
                </Stack>
                <Box sx={{ mt: 'auto' }}>
                  <Typography variant="caption" color="text.secondary" noWrap>out of 5.0</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={6} sm={6} md={3} sx={{ display: 'flex' }}>
            <Card sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ p: { xs: 1, sm: 1.25 }, '&:last-child': { pb: { xs: 1, sm: 1.25 } }, flex: 1, display: 'flex', flexDirection: 'column' }}>
                <Typography color="text.secondary" gutterBottom variant="caption" noWrap>5 Star Reviews</Typography>
                <Typography variant="h5" component="div" color="text.primary"
                  sx={{ fontSize: { xs: '1rem', sm: '1.125rem', md: '1.375rem' }, mb: 0.5 }}>
                  {stats.ratingDistribution[5]}
                </Typography>
                <Box sx={{ mt: 'auto' }}>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {stats.total > 0
                      ? `${((stats.ratingDistribution[5] / stats.total) * 100).toFixed(1)}% of total`
                      : '—'}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={6} sm={6} md={3} sx={{ display: 'flex' }}>
            <Card sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ p: { xs: 1, sm: 1.25 }, '&:last-child': { pb: { xs: 1, sm: 1.25 } }, flex: 1, display: 'flex', flexDirection: 'column' }}>
                <Typography color="text.secondary" gutterBottom variant="caption" noWrap>1 Star Reviews</Typography>
                <Typography variant="h5" component="div" color="text.primary"
                  sx={{ fontSize: { xs: '1rem', sm: '1.125rem', md: '1.375rem' }, mb: 0.5 }}>
                  {stats.ratingDistribution[1]}
                </Typography>
                <Box sx={{ mt: 'auto' }}>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {stats.total > 0
                      ? `${((stats.ratingDistribution[1] / stats.total) * 100).toFixed(1)}% of total`
                      : '—'}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* ── Search & Filters ── */}
        <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 2 }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            justifyContent="space-between"
          >
            <TextField
              placeholder="Search reviews... (Press Enter)"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyPress={handleSearchKeyPress}
              sx={{ flexGrow: 1, maxWidth: { sm: 400 } }}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start"><SearchIcon /></InputAdornment>
                ),
              }}
            />
            <Stack direction="row" alignItems="center" spacing={1} sx={{ flexShrink: 0 }}>
              <Button
                onClick={() => setShowFilters(!showFilters)}
                variant={hasActiveFilters ? 'contained' : 'outlined'}
                startIcon={<FilterListIcon />}
                endIcon={showFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                size="small"
              >
                Filters {hasActiveFilters && `(${activeFiltersCount})`}
              </Button>
              {hasActiveFilters && (
                <Button onClick={clearFilters} variant="text" size="small" startIcon={<ClearIcon />}>
                  Clear
                </Button>
              )}
            </Stack>
          </Stack>

          <Collapse in={showFilters}>
            <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Rating</InputLabel>
                    <Select value={ratingFilter} onChange={(e) => handleRatingFilterChange(e.target.value)} label="Rating">
                      <MenuItem value="all">All Ratings</MenuItem>
                      <MenuItem value="5">5 Stars ({stats.ratingDistribution[5]})</MenuItem>
                      <MenuItem value="4">4 Stars ({stats.ratingDistribution[4]})</MenuItem>
                      <MenuItem value="3">3 Stars ({stats.ratingDistribution[3]})</MenuItem>
                      <MenuItem value="2">2 Stars ({stats.ratingDistribution[2]})</MenuItem>
                      <MenuItem value="1">1 Star ({stats.ratingDistribution[1]})</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={9}>
                  <Box sx={{ display: 'flex', alignItems: 'center', height: '40px' }}>
                    <Typography variant="body2" color="text.secondary">
                      {hasActiveFilters
                        ? `Found ${reviews.length} reviews matching your filters`
                        : `Showing ${reviews.length} reviews on this page`}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Collapse>
        </Paper>
      </Box>

      {/* ── Scrollable Body ── */}
      <Box
        sx={{
          flex: 1,
          overflow: { xs: 'visible', md: 'auto' },
          display: 'flex',
          flexDirection: 'column',
          minHeight: { xs: 'auto', md: 0 },
        }}
      >
        {reviews.length === 0 && !loading && (
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
            <Card sx={{ width: '100%', maxWidth: 500 }}>
              <CardContent sx={{ textAlign: 'center', py: 6 }}>
                <RateReviewIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2, opacity: 0.5 }} />
                <Typography variant="h5" color="text.secondary" gutterBottom>
                  {hasActiveFilters ? 'No Reviews Match Your Filters' : 'No Reviews Found'}
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 400, mx: 'auto' }}>
                  {hasActiveFilters
                    ? 'Try adjusting your search or filter criteria.'
                    : 'No customer reviews available yet.'}
                </Typography>
                {hasActiveFilters && (
                  <Button onClick={clearFilters} variant="outlined" startIcon={<ClearIcon />}>
                    Clear Filters
                  </Button>
                )}
              </CardContent>
            </Card>
          </Box>
        )}

        {reviews.length > 0 && (
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: { xs: 'auto', md: 0 } }}>
            <TableContainer
              component={Paper}
              ref={tableWrapperRef}
              sx={{
                flex: 1,
                overflow: { xs: 'auto', md: 'auto' },
                maxHeight: { md: '100%' },
                mb: 2,
                position: 'relative',
                '& .MuiTable-root': { minWidth: { xs: 480, md: 800 } },
              }}
            >
              <Table stickyHeader size={isMobile ? 'small' : 'medium'}>
                <TableHead>
                  <TableRow>
                    <TableCell>Customer</TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Product</TableCell>
                    <TableCell>Rating</TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Comment</TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Date</TableCell>
                    {(canView || canManage) && (
  <TableCell align="right">Actions</TableCell>
)}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reviews.map((review) => (
                    <TableRow
                      key={review.id}
                      hover
                      sx={{
                        '&:hover': { backgroundColor: 'action.hover' },
                        '&:last-child td, &:last-child th': { borderBottom: 0 },
                      }}
                    >
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Avatar
                            src={review.imageUrl}
                            sx={{ width: { xs: 28, sm: 32 }, height: { xs: 28, sm: 32 }, bgcolor: 'primary.main', flexShrink: 0 }}
                          >
                            {!review.imageUrl && <PersonIcon fontSize="small" />}
                          </Avatar>
                          <Box minWidth={0}>
                            <Typography variant="body2"
                              sx={{ fontWeight: 600, fontSize: { xs: '0.8rem', sm: '0.875rem' } }} noWrap>
                              {review.userName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                              {review.userEmail}
                            </Typography>
                            <Typography variant="caption" color="text.secondary"
                              sx={{ display: { xs: 'block', sm: 'none' } }} noWrap>
                              {review.productName}
                            </Typography>
                          </Box>
                        </Stack>
                      </TableCell>

                      <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>{review.productName}</Typography>
                        {/* <Typography variant="caption" color="text.secondary">
                          ID: {review.productId.substring(0, 8)}...
                        </Typography> */}
                      </TableCell>

                      <TableCell>
                        <Chip
                          label={`${review.rating} ★`}
                          size="small"
                          variant="outlined"
                          sx={getSoftChipSx(getRatingChipColor(review.rating), isDark)}
                        />
                      </TableCell>

                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                        <Typography variant="body2"
                          sx={{ maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {review.comment || 'No comment'}
                        </Typography>
                      </TableCell>

                      <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                        <Typography variant="body2" color="text.secondary">
                          {review.createdAt.toLocaleDateString()}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {review.createdAt.toLocaleTimeString()}
                        </Typography>
                      </TableCell>

{(canView || canManage) && (
                      <TableCell align="right">
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end" flexWrap="nowrap">
                          <Tooltip title="View Details">
                            <IconButton onClick={() => handleViewReview(review)} size="small" color="primary">
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {canManage && (
                          <Tooltip title="Delete Review">
                            <IconButton
                              onClick={() => handleDeleteReview(review)}
                              disabled={deleteLoading === review.id}
                              size="small"
                              color="error"
                            >
                              {deleteLoading === review.id
                                ? <CircularProgress size={16} />
                                : <DeleteIcon fontSize="small" />}
                            </IconButton>
                          </Tooltip>
                          )}
                        </Stack>
                      </TableCell>
)}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {loading && <LoadingOverlay wrapperRef={tableWrapperRef} />}

            <Paper elevation={2} sx={{ flexShrink: 0, borderRadius: 1 }}>
              <Box sx={{ py: 2, px: 2 }}>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={{ xs: 1.5, sm: 3 }}
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ width: '100%' }}
                >
                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                    Showing {totalReviews === 0 ? 0 : (currentPage - 1) * REVIEWS_PER_PAGE + 1}–
                    {Math.min(currentPage * REVIEWS_PER_PAGE, totalReviews)} of {totalReviews} reviews
                  </Typography>
                  <Pagination
                    count={Math.max(1, totalPages)}
                    page={currentPage}
                    onChange={handlePageChange}
                    color="primary"
                    size={isMobile ? 'small' : 'medium'}
                    showFirstButton
                    showLastButton
                    disabled={loading || totalPages <= 1}
                  />
                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                    Page {currentPage} of {Math.max(1, totalPages)}
                  </Typography>
                </Stack>
              </Box>
            </Paper>
          </Box>
        )}
      </Box>

      {/* ── Review Detail Drawer ── */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={handleCloseDrawer}
        PaperProps={{ sx: { width: { xs: '100%', sm: 520 } } }}
      >
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Box
            sx={{
              px: 3, py: 2,
              borderBottom: 1, borderColor: 'divider',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              flexShrink: 0,
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <RateReviewIcon color="primary" />
              <Typography variant="h6">Review Details</Typography>
            </Stack>
            <IconButton onClick={handleCloseDrawer} edge="end"><CloseIcon /></IconButton>
          </Box>

          <Box
            sx={{
              flex: 1, overflow: 'auto', px: 3, py: 3, minHeight: 0,
              '&::-webkit-scrollbar': { width: '6px' },
              '&::-webkit-scrollbar-track': { background: 'transparent' },
              '&::-webkit-scrollbar-thumb': { background: 'rgba(0,0,0,0.2)', borderRadius: '3px' },
              '&::-webkit-scrollbar-thumb:hover': { background: 'rgba(0,0,0,0.35)' },
            }}
          >
            {selectedReview && (
              <Stack spacing={3}>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Avatar src={selectedReview.imageUrl}
                    sx={{ width: 56, height: 56, bgcolor: 'primary.main', flexShrink: 0 }}>
                    {!selectedReview.imageUrl && <PersonIcon sx={{ fontSize: 28 }} />}
                  </Avatar>
                  <Box minWidth={0}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }} noWrap>{selectedReview.userName}</Typography>
                    <Typography variant="body2" color="text.secondary" noWrap>{selectedReview.userEmail}</Typography>
                  </Box>
                </Stack>

                <Divider />

                <Box>
                  <Typography variant="caption" color="text.secondary"
                    sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>Product</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600, mt: 0.5 }}>{selectedReview.productName}</Typography>
                  <Typography variant="caption" color="text.secondary">ID: {selectedReview.productId}</Typography>
                </Box>

                <Divider />

                <Box>
                  <Typography variant="caption" color="text.secondary"
                    sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>Rating</Typography>
                  <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mt: 0.5 }}>
                    <Rating value={selectedReview.rating} readOnly />
                    <Chip
                      label={`${selectedReview.rating} ★`}
                      size="small"
                      variant="outlined"
                      sx={getSoftChipSx(getRatingChipColor(selectedReview.rating), isDark)}
                    />
                  </Stack>
                </Box>

                <Divider />

                <Box>
                  <Typography variant="caption" color="text.secondary"
                    sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>Submitted On</Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {selectedReview.createdAt.toLocaleDateString(undefined, {
                      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
                    })}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {selectedReview.createdAt.toLocaleTimeString()}
                  </Typography>
                </Box>

                <Divider />

                <Box>
                  <Typography variant="caption" color="text.secondary"
                    sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>Comment</Typography>
                  <Paper
                    sx={{
                      p: 2, mt: 1,
                      bgcolor: 'action.hover',
                      border: '1px solid', borderColor: 'divider', borderRadius: 1,
                    }}
                  >
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>
                      {selectedReview.comment || 'No comment provided'}
                    </Typography>
                  </Paper>
                </Box>
              </Stack>
            )}
          </Box>

          <Divider />
          <Box sx={{ px: 3, py: 2, display: 'flex', gap: 1.5, justifyContent: 'flex-end', flexShrink: 0 }}>
            {selectedReview && canManage && (
              <Button
                onClick={() => handleDeleteReview(selectedReview)}
                variant="outlined"
                color="error"
                size="small"
                disabled={deleteLoading === selectedReview.id}
                startIcon={deleteLoading === selectedReview.id ? <CircularProgress size={14} /> : <DeleteIcon />}
              >
                Delete Review
              </Button>
            )}
            <Button onClick={handleCloseDrawer} variant="contained" size="small">Close</Button>
          </Box>
        </Box>
      </Drawer>

      {/* ── Delete Confirmation Dialog ── */}
      <Dialog open={!!deleteTarget} onClose={handleDeleteCancel} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Review</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the review by{' '}
            <strong>"{deleteTarget?.userName}"</strong>?{' '}
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleDeleteCancel} disabled={deleteDialogLoading}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteConfirm}
            disabled={deleteDialogLoading}
            startIcon={deleteDialogLoading ? <CircularProgress size={16} /> : <DeleteIcon />}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Full-screen deleting overlay (mirrors EnhancedProductDrawer) ── */}
      <Backdrop
        open={deletingOverlay}
        sx={{
          zIndex: (t) => t.zIndex.modal + 100,
          flexDirection: 'column',
          gap: 3,
          bgcolor: 'rgba(0,0,0,0.6)',
        }}
      >
        <Box
          sx={{
            bgcolor: 'background.paper',
            borderRadius: 3,
            px: 5,
            py: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2.5,
            minWidth: 280,
            boxShadow: 24,
          }}
        >
          <CircularProgress size={48} thickness={4} />
          <Box sx={{ width: '100%' }}>
            <LinearProgress sx={{ borderRadius: 1, mb: 1.5 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600, textAlign: 'center', color: 'text.primary' }}>
              Deleting Review…
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 0.5 }}>
              Please wait, do not close this window
            </Typography>
          </Box>
        </Box>
      </Backdrop>

      <ToastNotification
        open={toast.open}
        message={toast.message}
        severity={toast.severity}
        onClose={handleCloseToast}
      />
    </Box>
  );
};

export default ReviewsManagement;